import type { LayerSpec } from '@/core/pipeline';
import { nearestIndex, parseHex, type RGB } from '@/lib/color';
import {
  DEFAULT_PALETTE_ID,
  getPalette,
  paletteIds,
  paletteToRGB,
} from './lospec';

type PaletteParams = {
  palette: string;
  custom?: string[];
};

function resolvePalette(p: PaletteParams): RGB[] {
  if (p.palette === 'custom' && Array.isArray(p.custom)) {
    return p.custom
      .map((c) => parseHex(c))
      .filter((c): c is RGB => c !== null);
  }
  const def = getPalette(p.palette);
  if (def) return paletteToRGB(def);
  const fallback = getPalette(DEFAULT_PALETTE_ID);
  return fallback ? paletteToRGB(fallback) : [];
}

export const paletteSpec: LayerSpec = {
  kind: 'palette',
  category: 'filter',
  animated: false,
  label: 'Palette',
  defaults: { palette: DEFAULT_PALETTE_ID } satisfies PaletteParams,
  schema: {
    palette: {
      type: 'select',
      label: 'Palette',
      options: [...paletteIds(), 'custom'],
      default: DEFAULT_PALETTE_ID,
    },
  },
  run: (input, params) => {
    if (input === null) {
      // Pipeline already skips filters with no upstream, but be defensive.
      return new ImageData(1, 1);
    }
    const palette = resolvePalette(params as PaletteParams);
    if (palette.length === 0) return input;

    const out = new ImageData(input.width, input.height);
    const src = input.data;
    const dst = out.data;
    for (let i = 0; i < src.length; i += 4) {
      const a = src[i + 3] as number;
      if (a === 0) continue;
      const idx = nearestIndex(
        [src[i] as number, src[i + 1] as number, src[i + 2] as number],
        palette,
      );
      const c = palette[idx] as RGB;
      dst[i]     = c[0];
      dst[i + 1] = c[1];
      dst[i + 2] = c[2];
      dst[i + 3] = a;
    }
    return out;
  },
};
